import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { useAvatarContext } from '@avatar/context/AvatarProvider';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import { AppState } from 'store';
import { useAppDispatch } from 'store/hooks';
import { setFormattedTextDrafts } from 'store/ScriptStudioBuffersSlice';
import { EnhancedPhoneticReplacement } from 'store/ReplacementsSlice';
import useProjectSections from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import { buildSectionView } from 'components/ScriptStudio/Sections/selectors';
import useGetSectionsFormattedText from 'components/ScriptStudio/Sidebar/hooks/useGetSectionsFormattedText';
import {
  buildReplacementsWithRegex,
  deduplicateByText,
  ReplacementWithRegex,
} from 'components/ScriptStudio/Editor/extensions/ReplacementExtension';
import HeadlessReplacementManager from 'components/ScriptStudio/Editor/extensions/headlessReplacementManager';
import { Section } from '../../../../server/graphql/__generated.types';
import applyReplacementDiff from '../utils/headlessReplacementTransform';

type QueryAvatar = ReturnType<typeof useAvatarContext>['queryAvatar'];
type SectionDrafts = AppState['scriptStudioBuffers']['byId'];

const NO_ADDED_REPLACEMENTS: Map<string, EnhancedPhoneticReplacement> =
  new Map();
const NO_ADDED_REPLACEMENTS_WITH_REGEX: ReplacementWithRegex[] = [];

/*
 * Diff two replacement lists by id, returning the ones that were added and the
 * ids of the ones that were removed. Replacements are immutable, so an "edit"
 * shows up as a removal plus an addition.
 */
const diffReplacements = (
  prevReplacements: EnhancedPhoneticReplacement[],
  nextReplacements: EnhancedPhoneticReplacement[]
) => {
  const prevIds = new Set(prevReplacements.map(({ id }) => id));
  const nextIds = new Set(nextReplacements.map(({ id }) => id));

  return {
    added: nextReplacements.filter(({ id }) => !prevIds.has(id)),
    removedIds: new Set([...prevIds].filter(id => !nextIds.has(id))),
  };
};

/*
 * Whether a section's avatar can render replacement marks.
 *
 * Adding a replacement from the side rail writes marks into every section's
 * buffer, so this is what keeps marks out of sections whose voice does not
 * support them. The side rail's own `disabled` state only reflects the
 * *active* section, which is not enough in a mixed-voice script.
 *
 * @note ability gating only applies to universal projects — other studio
 *   versions are gated upstream by useEvaluateStudioProjectVersionFeatures.
 * @note a section is only excluded when we positively know its avatar cannot
 *   render replacements. queryAvatar returns `abilities: null` whenever the
 *   variant or model version is missing from the avatar cache, and that must
 *   not silently stop replacements from syncing.
 */
export const sectionSupportsReplacements = ({
  section,
  draft,
  queryAvatar,
}: {
  section: Section;
  draft?: SectionDrafts[string];
  queryAvatar: QueryAvatar;
}): boolean => {
  // buildSectionView resolves draft-over-take avatar precedence, so an
  // unrendered voice swap is respected before the take catches up.
  const { avatar, avatarVariant, avatarVariantVersion } = buildSectionView(
    section,
    draft
  );

  const { abilities } = queryAvatar({
    avatarId: avatar,
    variantId: avatarVariant,
    modelVersion: avatarVariantVersion || undefined,
    withAbilities: true,
  });

  return !(abilities && !abilities.supportsReplacements);
};

export const buildSupportsReplacementsBySectionId = ({
  sections,
  draftsById,
  queryAvatar,
}: {
  sections: Section[];
  draftsById: SectionDrafts;
  queryAvatar: QueryAvatar;
}): Map<string, boolean> =>
  new Map(
    sections.map(section => [
      section.id,
      sectionSupportsReplacements({
        section,
        draft: draftsById[section.id],
        queryAvatar,
      }),
    ])
  );

const useBulkUpdateReplacementsInScript = () => {
  const { projectId } = useStudioProjectContext();
  const dispatch = useAppDispatch();
  const store = useStore<AppState>();
  const { queryAvatar } = useAvatarContext();

  const { sections } = useProjectSections({ projectId });
  const allSectionIds = useMemo(() => sections.map(s => s.id), [sections]);
  const getSectionsFormattedText = useGetSectionsFormattedText();

  const applyBulkReplacementUpdate = useCallback(
    (
      prevReplacements: EnhancedPhoneticReplacement[],
      nextReplacements: EnhancedPhoneticReplacement[]
    ) => {
      const { added, removedIds } = diffReplacements(
        prevReplacements,
        nextReplacements
      );

      if (added.length === 0 && removedIds.size === 0) return;

      // Hoist dedup + regex compilation outside the per-section loop — both are constant across sections
      const addedByText = deduplicateByText(added);
      const addedWithRegex = buildReplacementsWithRegex(addedByText);
      const instance = new HeadlessReplacementManager();
      // Read drafts imperatively: this runs on user action, so recomputing
      // avatar support per render (drafts change on every keystroke) is waste.
      const supportsBySectionId = buildSupportsReplacementsBySectionId({
        sections,
        draftsById: store.getState().scriptStudioBuffers.byId,
        queryAvatar,
      });

      try {
        const updatedSections = getSectionsFormattedText(allSectionIds).flatMap(
          ({ sectionId, formattedText }) => {
            /*
             * Sections whose avatar cannot render replacements never receive
             * new marks, but removals still apply so marks left behind by an
             * earlier voice swap get cleaned up.
             */
            const supportsReplacements =
              supportsBySectionId.get(sectionId) ?? true;

            const result = applyReplacementDiff(
              formattedText,
              removedIds,
              supportsReplacements ? addedByText : NO_ADDED_REPLACEMENTS,
              instance,
              supportsReplacements
                ? addedWithRegex
                : NO_ADDED_REPLACEMENTS_WITH_REGEX
            );

            // Skip sections the update didn't touch so we don't create drafts for them
            return result === formattedText
              ? []
              : [{ id: sectionId, formattedText: result }];
          }
        );

        if (updatedSections.length > 0) {
          dispatch(
            setFormattedTextDrafts({ projectId, sections: updatedSections })
          );
        }
      } finally {
        instance.manager.destroy();
      }
    },
    [
      allSectionIds,
      dispatch,
      getSectionsFormattedText,
      projectId,
      queryAvatar,
      sections,
      store,
    ]
  );

  return { applyBulkReplacementUpdate };
};

export default useBulkUpdateReplacementsInScript;
